import { useScrollReveal } from "../../../hooks/useScrollReveal";

const benefits = [
  {
    icon: "ri-focus-3-line",
    title: "Never miss a lead",
    desc: "Every inquiry, contact, and signal is captured automatically—nothing falls through the cracks.",
  },
  {
    icon: "ri-repeat-2-line",
    title: "Automated follow-up",
    desc: "Consistent, timely follow-ups that execute on schedule without human intervention.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "Centralized operations",
    desc: "One unified system for every workflow, pipeline, and customer interaction.",
  },
  {
    icon: "ri-settings-4-line",
    title: "Repeatable workflows",
    desc: "Standardized systems that perform identically, every single time.",
  },
  {
    icon: "ri-line-chart-line",
    title: "Scale under pressure",
    desc: "Growth infrastructure designed to hold when volume increases.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Operational reliability",
    desc: "Systems built for long-term performance—months and years after deployment.",
  },
];

export default function BenefitsGrid() {
  const { ref: headRef, visible: headVisible } = useScrollReveal(0.1);

  return (
    <section className="w-full bg-[#07080C] py-36 px-6">
      {/* Header */}
      <div
        ref={headRef as React.RefObject<HTMLDivElement>}
        className="max-w-7xl mx-auto text-center mb-24"
        style={{
          opacity: headVisible ? 1 : 0,
          transform: headVisible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.9s ease, transform 0.9s ease",
        }}
      >
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#4ADE80]/80 mb-5 block">
          Core Benefits
        </span>
        <h2
          className="font-extrabold text-white leading-[0.95] tracking-[-0.04em] max-w-3xl mx-auto"
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(2.8rem, 5vw, 5rem)",
          }}
        >
          Everything You Need to Operate at Scale
        </h2>
      </div>

      {/* Feature visual — large center image */}
      <div className="max-w-7xl mx-auto mb-20 relative rounded-3xl overflow-hidden" style={{ height: "520px" }}>
        <img
          src="https://public.readdy.ai/ai/img_res/f6d9751ce9dc1b1408293ed476b30225.jpg"
          alt="Operational scale"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07080C] via-transparent to-[#07080C]/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07080C]/60 via-transparent to-transparent" />
        {/* Centered stat overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p
              className="font-black text-white/90 leading-none tracking-[-0.05em]"
              style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(4rem, 9vw, 9rem)" }}
            >
              Zero Gaps
            </p>
            <p className="text-white/40 text-lg tracking-widest uppercase mt-3" style={{ fontFamily: "'Inter', sans-serif" }}>
              in your operation
            </p>
          </div>
        </div>
      </div>

      {/* Benefits grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06] rounded-2xl overflow-hidden">
        {benefits.map((b, i) => (
          <BenefitCard key={b.title} {...b} delay={i * 0.08} />
        ))}
      </div>
    </section>
  );
}

function BenefitCard({ icon, title, desc, delay }: { icon: string; title: string; desc: string; delay: number }) {
  const { ref, visible } = useScrollReveal(0.1);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="bg-[#07080C] p-10 group hover:bg-[#0D0E14] transition-colors duration-300 cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      <div className="w-10 h-10 flex items-center justify-center mb-6">
        <i className={`${icon} text-[#4ADE80] text-2xl group-hover:scale-110 transition-transform duration-300`} />
      </div>
      <h3
        className="text-xl font-bold text-white mb-3 tracking-[-0.02em]"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {title}
      </h3>
      <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
    </div>
  );
}
